'use client';

import {
  CheckCircle,
  CircleNotch,
  Info,
  Warning,
  X,
  XCircle,
} from '@phosphor-icons/react/ssr';
import type { ComponentProps, ElementType, ReactNode } from 'react';
import { toast as sonnerToast } from 'sonner';

import { tv } from '../../lib/tv';
import { Toaster as ToasterPrimitive, type ToasterProps } from '../ui/sonner';
import { FeedbackKeyframes } from './keyframes';

export type ToastTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/**
 * The Figma Toast (74:4 / 77:63): 380 wide, Forest Ink surface (bg/inverse),
 * radius/lg, Shadow/LG; padding 12 / 12 / 12 / 16, gap space/3, top-aligned.
 * 20px icon in status/<tone>/on-inverse (Neutral: text/link-inverse) · Text
 * (gap 2): Title Label/Medium text/inverse, Description Body/Small
 * text/inverse at 78% · Action (Label/Small text/link-inverse, padding 4 / 6,
 * radius/sm) · Close 16px icon/inverse at 70% · Timer: a 2px bar along the
 * bottom in the icon colour at 60%, counting down the duration.
 */
const card = tv({
  slots: {
    root: 'bg-bg-inverse group/toast relative flex w-95 max-w-full items-start gap-3 overflow-hidden rounded-lg py-3 pr-3 pl-4 shadow-lg',
    icon: 'size-5 shrink-0',
    text: 'flex min-w-0 flex-1 flex-col gap-0.5',
    title: 'text-label-md text-text-inverse',
    description: 'text-body-sm text-text-inverse opacity-78',
    action: [
      'text-label-sm text-text-link-inverse shrink-0 rounded-sm px-1.5 py-1 outline-none',
      'transition-colors duration-[120ms] ease-out hover:bg-bg-inverse-hover active:bg-bg-inverse-pressed',
      'focus-visible:ring-2 focus-visible:ring-border-focus',
    ],
    close: [
      // 16px glyph, 32px hit area.
      'text-icon-inverse relative flex size-4 shrink-0 items-center justify-center rounded-xs opacity-70 outline-none',
      'after:absolute after:-inset-2 after:content-[""] hover:opacity-100',
      'focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-border-focus',
    ],
    timer: [
      'pointer-events-none absolute bottom-0 left-0 h-0.5 w-full origin-left opacity-60',
      'motion-safe:animate-[rp-toast-timer_linear_forwards] group-hover/toast:[animation-play-state:paused]',
      'motion-reduce:hidden',
    ],
  },
  variants: {
    tone: {
      neutral: { icon: 'text-text-link-inverse', timer: 'bg-text-link-inverse' },
      success: {
        icon: 'text-status-success-on-inverse',
        timer: 'bg-status-success-on-inverse',
      },
      warning: {
        icon: 'text-status-warning-on-inverse',
        timer: 'bg-status-warning-on-inverse',
      },
      danger: {
        icon: 'text-status-danger-on-inverse',
        timer: 'bg-status-danger-on-inverse',
      },
      info: { icon: 'text-status-info-on-inverse', timer: 'bg-status-info-on-inverse' },
    },
  },
  defaultVariants: { tone: 'neutral' },
});

/** Figma icon per Tone (Phosphor Regular). */
const TONE_ICON: Record<ToastTone, ElementType<{ className?: string }>> = {
  neutral: Info,
  success: CheckCircle,
  warning: Warning,
  danger: XCircle,
  info: Info,
};

export type ToastAction = {
  /** One short verb: Undo / View / Retry. */
  label: string;
  onClick: () => void;
};

export type ToastProps = Omit<ComponentProps<'div'>, 'title' | 'children'> & {
  /** Figma "Tone". */
  tone?: ToastTone;
  /** Figma "Title": one short line. */
  title: ReactNode;
  /** Figma "Description" (Show description). */
  description?: ReactNode;
  /** Figma "Action": at most one text action. */
  action?: ToastAction;
  /** Figma "Close": shows the ×. */
  onClose?: () => void;
  closeLabel?: string;
  /** Figma "Timer": a countdown bar over this many ms (paused on hover). */
  timer?: number;
  /** Defaults to the tone's icon; `false` hides it. */
  icon?: ElementType<{ className?: string }> | false;
  /** A spinner in place of the icon (`toast.loading`, `toast.promise`). */
  loading?: boolean;
};

/**
 * The toast card (Figma: Components ↳ Toast). Apps don't render it: call
 * `toast(…)` / `toast.success(…)` and mount one `<Toaster />`. Exported for
 * static layouts (docs, the Figma frame).
 */
export function Toast({
  tone = 'neutral',
  title,
  description,
  action,
  onClose,
  closeLabel = 'Close',
  timer,
  icon,
  loading = false,
  className,
  style,
  ...props
}: ToastProps) {
  const styles = card({ tone });
  const Icon = loading ? CircleNotch : icon === false ? null : (icon ?? TONE_ICON[tone]);
  return (
    <div
      data-slot="toast"
      data-tone={tone}
      className={styles.root({ className })}
      style={style}
      {...props}
    >
      {Icon && (
        <Icon
          aria-hidden
          data-slot="toast-icon"
          className={styles.icon({
            className: loading ? 'animate-spin motion-reduce:animate-none' : undefined,
          })}
        />
      )}
      <div data-slot="toast-text" className={styles.text()}>
        <div data-slot="toast-title" className={styles.title()}>
          {title}
        </div>
        {description && (
          <div data-slot="toast-description" className={styles.description()}>
            {description}
          </div>
        )}
      </div>
      {action && (
        <button
          type="button"
          data-slot="toast-action"
          className={styles.action()}
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}
      {onClose && (
        <button
          type="button"
          aria-label={closeLabel}
          data-slot="toast-close"
          className={styles.close()}
          onClick={onClose}
        >
          <X aria-hidden className="size-4" />
        </button>
      )}
      {timer != null && Number.isFinite(timer) && (
        <>
          <FeedbackKeyframes />
          <span
            aria-hidden
            data-slot="toast-timer"
            className={styles.timer()}
            style={{ animationDuration: `${timer}ms` }}
          />
        </>
      )}
    </div>
  );
}

/** Auto-dismiss after 5s (Figma); errors and loading persist until closed / settled. */
export const TOAST_DURATION = 5000;

export type ToastOptions = {
  /** Reuse an id to update a toast in place. */
  id?: string | number;
  description?: ReactNode;
  action?: ToastAction;
  /** ms; `Infinity` keeps it until closed. Default 5000 (danger: Infinity). */
  duration?: number;
  /** The × (Figma "Close"). Default on. */
  closeButton?: boolean;
  /** Show the countdown bar (Figma "Timer"). */
  timer?: boolean;
  icon?: ElementType<{ className?: string }> | false;
  onDismiss?: () => void;
  onAutoClose?: () => void;
};

function show(
  tone: ToastTone,
  title: ReactNode,
  options: ToastOptions = {},
  loading = false,
) {
  const { id, description, action, closeButton = true, timer, icon } = options;
  const duration =
    options.duration ?? (tone === 'danger' || loading ? Infinity : TOAST_DURATION);
  return sonnerToast.custom(
    (toastId) => (
      <Toast
        tone={tone}
        title={title}
        description={description}
        icon={icon}
        loading={loading}
        timer={timer ? duration : undefined}
        action={
          action && {
            label: action.label,
            onClick: () => {
              action.onClick();
              sonnerToast.dismiss(toastId);
            },
          }
        }
        onClose={closeButton ? () => sonnerToast.dismiss(toastId) : undefined}
      />
    ),
    { id, duration, onDismiss: options.onDismiss, onAutoClose: options.onAutoClose },
  );
}

type Message<T> = ReactNode | ((value: T) => ReactNode);
const resolve = <T,>(message: Message<T>, value: T): ReactNode =>
  typeof message === 'function' ? (message as (value: T) => ReactNode)(value) : message;

export type ToastPromiseMessages<T> = {
  loading: ReactNode;
  success: Message<T>;
  error: Message<unknown>;
  /** Options for the settled toast (description, action, …). */
  successOptions?: Omit<ToastOptions, 'id'>;
  errorOptions?: Omit<ToastOptions, 'id'>;
};

/**
 * Figma-styled toasts on sonner's queue (Figma: Components ↳ Toast).
 * `toast(title, options)` is Neutral; `.success` / `.warning` / `.info` /
 * `.error` pick the tone (errors persist until closed); `.loading` shows a
 * spinner until you update it by `id`; `.promise` goes loading → success /
 * error in the same toast. Returns the toast id; `.dismiss(id?)` closes.
 * Needs one `<Toaster />` mounted.
 */
export const toast = Object.assign(
  (title: ReactNode, options?: ToastOptions) => show('neutral', title, options),
  {
    success: (title: ReactNode, options?: ToastOptions) =>
      show('success', title, options),
    warning: (title: ReactNode, options?: ToastOptions) =>
      show('warning', title, options),
    info: (title: ReactNode, options?: ToastOptions) => show('info', title, options),
    error: (title: ReactNode, options?: ToastOptions) => show('danger', title, options),
    loading: (title: ReactNode, options?: ToastOptions) =>
      show('neutral', title, options, true),
    promise: <T,>(
      promise: Promise<T> | (() => Promise<T>),
      messages: ToastPromiseMessages<T>,
    ) => {
      const id = show('neutral', messages.loading, {}, true);
      const run = typeof promise === 'function' ? promise() : promise;
      run.then(
        (value) =>
          show('success', resolve(messages.success, value), {
            ...messages.successOptions,
            id,
          }),
        (error: unknown) =>
          show('danger', resolve(messages.error, error), {
            ...messages.errorOptions,
            id,
          }),
      );
      return id;
    },
    dismiss: (id?: string | number) => sonnerToast.dismiss(id),
  },
);

/**
 * Where toasts appear: mount once near the app root. Top-right, newest in
 * front, at most 3 visible, 5s auto-dismiss paused on hover; the list is a
 * polite live region ("Notifications", Alt+T moves focus to it).
 */
export function Toaster(props: ToasterProps) {
  return <ToasterPrimitive {...props} />;
}

export type { ToasterProps };
